import Config from 'config';
import _ from 'lodash';
import { useState } from 'react';
import { Button as BootstrapButton, ButtonProps, Spinner } from 'react-bootstrap';
import { Link as ImportLink, LinkProps as ImportLinkProps } from 'react-router-dom';
import styled, { FlattenInterpolation, FlattenSimpleInterpolation, ThemeProps, css } from 'styled-components';

import { convertHex } from 'utils/colors-helper';
import { sleep, togglePush } from 'utils/common-helper';

import useResources from 'hooks/useResources';

import imgIconsCheckMarkWhite from 'assets/icons/check_mark_white.svg';

const { isMarketing } = Config;

export type TExtra =
    | string
    | FlattenSimpleInterpolation
    | FlattenInterpolation<ThemeProps<any>>
    | ((theme: any) => string)
    | ((theme: any) => FlattenSimpleInterpolation);

const A = styled.a`
    text-decoration: none;
    &:hover {
        text-decoration: none;
    }
`;

interface LinkProps extends ImportLinkProps {
    withoutScroll?: boolean;
    marketing?: boolean;
    support?: boolean;
    absolute?: boolean;
    [propName: string]: any;
}

const StyledLink = styled(ImportLink)<LinkProps>`
    text-decoration: none;
    &:hover {
        text-decoration: none;
    }
`;

export const Link = (props: LinkProps) => {
    const {
        withoutScroll = false,
        marketing = false,
        support = false,
        absolute = false,
        to = `/`,
        target = '_self',
        selectable = false,
        ...restProps
    } = props;
    if (isMarketing || support || marketing || absolute) {
        return (
            <A
                href={`${
                    absolute
                        ? ''
                        : marketing || support
                        ? Config?.marketing?.[support ? `supportLink` : `baseUrl`]
                        : Config?.application?.baseUrl
                }${to}`}
                target={target}
                // rel="noopener noreferrer"
                {...restProps}
            >
                {props.children}
            </A>
        );
    }
    return (
        <StyledLink
            to={to}
            {...restProps}
            onClick={(e: any) => {
                if (!withoutScroll) {
                    try {
                        document?.getElementById(`scrollWrapper`)?.scrollTo({ top: 0, behavior: 'smooth' });
                    } catch (error) {}
                    try {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    } catch (error) {}
                    if (_.isFunction(props?.onClick)) {
                        props?.onClick?.(e);
                    }
                }
            }}
            href={`${Config.marketing[support ? `supportLink` : `baseUrl`]}${to}`}
        />
    );
};

export const screenSizes = {
    xs: `0px`,
    sm: `576px`,
    md: `768px`,
    lg: `992px`,
    xl: `1200px`,
};

export const Frame = styled.div<{ extra?: any; row?: boolean; className?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    font-size: 16px;
    line-height: 24px;
    color: #212529;
    transition: 0.2s;

    ${(props: any) => props.row && `flex-direction: row;`}
    ${(props: any) => props.extra}
`;

export const Div = styled.div<{ extra?: TExtra; className?: string }>`
    ${({ extra }) => extra}
`;

export const Row = styled(Frame)<{ extra?: any }>`
    flex-direction: row;

    ${({ extra }) => extra};
`;

export const Checkbox = styled.input.attrs((props: any) => ({
    ...props,
    type: 'checkbox',
    onClick: props.muted ? () => {} : props.onClick,
    checked: props.selected || props.checked,
    disabled: props.muted || props.disabled,
    value: props.selected || props.checked ? 'on' : 'off',
}))<{ extra?: string; selected?: boolean; muted?: boolean }>`
    width: 16px;
    height: 16px;
    border-radius: 4px;
    margin-right: 8px;
    vertical-align: middle;
    border: 1px solid #ddd;
    appearance: none;
    -webkit-appearance: none;
    outline: none;
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;

    &:after {
        content: '';
        width: 12px;
        height: 12px;
        transition: 0.2s;
        background: url('${imgIconsCheckMarkWhite}') no-repeat center center / contain;
        visibility: ${(props: any) => (props.selected ? `visible` : `hidden`)};
        opacity: 0;
        transform: translateY(${(props: any) => (props.selected ? 0 : 10)}px);
    }

    ${({ selected }) =>
        selected &&
        css`
            background: ${({ theme }) => theme?.primary};
            &:after {
                opacity: 1;
            }
        `}

    ${({ extra }: any) => extra}
`;

type MyButtonProps = ButtonProps & {
    extra?: TExtra;
    extendedIndicators?: boolean;
    variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | string;
    disabled?: boolean;
    successLabel?: string;
    errorLabel?: string;
    type?: string;
};

export const Button = (props: MyButtonProps) => {
    const {
        extendedIndicators = false,
        variant = 'primary',
        onClick,
        disabled = false,
        successLabel,
        errorLabel,
    } = props;
    const { t } = useResources();
    const [status, setStatus] = useState('regular');
    return (
        <ButtonWrapper
            {...props}
            variant={
                (
                    {
                        success: 'success',
                        error: 'danger',
                    } as { [index: string]: any }
                )[status] ??
                variant ??
                'primary'
            }
            onClick={async (e: any) => {
                if (!onClick) {
                    return;
                }
                try {
                    setStatus(`pending`);
                    await onClick(e);
                    setStatus(extendedIndicators ? `success` : `regular`);
                } catch (error) {
                    setStatus(extendedIndicators ? `error` : `regular`);
                } finally {
                    if (extendedIndicators) {
                        await sleep(1000);
                        setStatus(`retular`);
                    }
                }
            }}
            disabled={disabled === true || [`pending`, `success`, `error`].includes(status)}
        >
            {(
                {
                    pending: <Spinner animation="border" size="sm" />,
                    success: successLabel ?? t(`ui.buttonSuccessLabel`),
                    error: errorLabel ?? t(`ui.buttonErrorLabel`),
                } as { [index: string]: any }
            )[status] ?? props?.children}
        </ButtonWrapper>
    );
};

const ButtonWrapper = styled(BootstrapButton)<{ extra?: string }>`
    font-size: 16px;

    @media (max-width: 375px) {
        font-size: 14px;
    }

    ${({ extra = `` }: any) => extra}
`;

export const H1 = styled(Frame)<{ extra?: string }>`
    margin-bottom: 15px;
    /*font-family: Marcellus, serif;*/
    font-size: 30px;
    line-height: 45px;
    color: ${(props: any) => props.theme.primary_action};

    ${({ extra = `` }: any) => extra}
`;

export const H2 = styled(H1)`
    font-size: 17px;
    line-height: 21px;
`;

export const P = styled(Frame)<{ extra?: string }>`
    width: 100%;
    /*font-family: Open Sans;*/
    font-size: 16px;
    line-height: 22px;
    color: ${(props: any) => props.theme.dark};
    text-align: left;
    align-items: flex-start;

    ${({ extra }: any) => extra}
`;

export const Switch = styled(Frame)<{ extra?: string; selected?: any }>`
    width: 39px;
    height: 21px;
    background: ${(props: any) => convertHex(`#E7E7E7`, props.selected ? 1 : 0.4)};
    border: 1px solid #e7e7e7;
    border-radius: 14.2725px;
    position: relative;

    &:after {
        content: '';
        width: 18px;
        height: 18px;
        position: absolute;
        left: ${(props: any) => (props.selected ? `calc(100% - 18px - 2px)` : `2px`)};
        background: ${(props: any) => (props.selected ? props?.theme?.maolive : `#ACACAC`)};
        border-radius: 12px;
        transition: 0.2s;
    }
`;

export const MultipleSelect = (props: any) => {
    const { options = [], muted = false, selected = [], onChange = () => {} } = props;
    return options?.map((item: any, index: number) => {
        return (
            <Field
                key={index}
                muted={muted}
                extra={props?.extra ?? ``}
                onClick={() => {
                    if (muted !== true) {
                        onChange(togglePush(selected ?? [], item.value));
                    }
                }}
            >
                <Checkbox selected={(selected ?? []).includes(item.value)} />
                {item.label}
            </Field>
        );
    });
};

export const Field = styled(Frame)<{ extra?: TExtra; muted?: boolean }>`
    flex-direction: row;
    margin-top: 15px;
    cursor: pointer;
    font-weight: 500;
    ${({ muted }) => (muted ? `opacity: 0.3; cursor: default;` : null)}
`;

export const RowWrapper = styled(Frame)<{ extra?: string }>`
    width: 100%;
    flex-direction: row;
    justify-content: space-between;

    ${(props: any) => props.extra}
`;

export const NotificationsMark = styled(Frame)<{ background?: string; count?: number | string; extra?: string }>`
    background: ${({ theme, background = `red` }: any) => convertHex(_.get(theme, background), 0.9)};
    border: 1px solid ${({ theme, background = `red` }: any) => _.get(theme, background)};
    box-sizing: border-box;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    margin-left: 6px;

    ${({ count = 0 }: any) => css`
        &:after {
            content: '${count}';
            color: white;
            font-size: 11px;
            line-height: 15px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: bold;
        }
    `}

    ${(props: any) => props.extra}
`;

export const Hint = styled(Frame)<{ extra?: string }>`
    font-size: 12px;
    align-items: flex-start;
    color: ${({ theme }) => theme.text.secondary};
`;

export const Scrollable = ({
    children,
    outerExtra = ``,
    innerExtra = ``,
}: {
    children?: any;
    outerExtra?: TExtra;
    innerExtra?: TExtra;
}) => {
    return (
        <Frame className="scrollable-outer" extra={`width: inherit; min-height: min-content; ${outerExtra}`}>
            <Frame
                className="scrollable-inner"
                extra={`width: inherit; flex: 1; overflow: auto; justify-content: flex-start; ${innerExtra}`}
            >
                {children}
            </Frame>
        </Frame>
    );
};

export const Container = styled(Frame)`
    width: 86vw;
    max-width: 1280px;

    @media (max-width: 991px) {
        width: 100%;
        max-width: calc(100vw - 7px * 2);
    }

    ${({ extra }) => extra}
`;

export const UnreliableLink = (props: any) => {
    if (props.to) {
        return <Link {...props} />;
    } else {
        return props.children;
    }
};
